import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, Menu, MessageSquare, Search } from 'lucide-react';
import { findNavItem } from '@/config/navigation';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { InboxAlertsBubble } from '@/features/alerts/InboxAlertsBubble';
import {
  describeAccesoInbox,
  describeBurbujaAlertas,
  type InboxAlertas,
} from '@/features/alerts/alerts-format';
import { ROUTES } from '@/routes/paths';
import { UserMenu } from './UserMenu';

/**
 * Header del panel.
 *
 * Distribución de la referencia visual: título de la sección a la izquierda (derivado de
 * la navegación, así ninguna pantalla tiene que repetirlo), luego el buscador, los
 * accesos rápidos de mensajes/notificaciones y, al final, el bloque de usuario.
 *
 * Es `sticky` con fondo semitransparente: el contenido largo (tablas, kanban) scrollea
 * por debajo sin que se pierda el contexto.
 *
 * El acceso de MENSAJES ya está vivo y lleva a Conversaciones con la burbuja de mensajes sin ver
 * encima (`features/alerts`): es el aviso que se ve desde cualquier pantalla. El de notificaciones
 * sigue deshabilitado porque no hay un módulo de notificaciones detrás (el bot pausado y las
 * novedades del inbox se cuentan en la misma burbuja y en el aviso flotante).
 */
export interface HeaderProps {
  onOpenMobileNav: () => void;
  /** Avisos del inbox: burbuja de mensajes sin ver sobre el icono de Mensajes. */
  alertas: InboxAlertas;
}

export function Header({ onOpenMobileNav, alertas }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentItem = findNavItem(location.pathname);

  const burbuja = describeBurbujaAlertas(alertas.nuevas.length);

  return (
    <header className="sticky top-0 z-30 flex h-18 items-center gap-3 border-b border-line bg-surface/95 px-4 backdrop-blur sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Abrir menú de navegación"
        onClick={onOpenMobileNav}
        className="lg:hidden"
      >
        <Menu size={20} />
      </Button>

      <h1 className="truncate text-page font-bold text-ink">{currentItem?.label ?? 'Panel'}</h1>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5">
        {/* TODO(siguiente chat): la búsqueda global se conecta cuando existan las pantallas
            de Órdenes/Productos (hoy no hay endpoint de búsqueda en el backend). */}
        <div className="hidden md:block md:w-56 lg:w-72">
          <Input
            type="search"
            leftIcon={<Search size={16} />}
            placeholder="Buscar pedido, producto…"
            aria-label="Buscar"
          />
        </div>

        {/* Conversaciones: el icono lleva a la bandeja y la burbuja dice cuántas conversaciones
            tienen mensajes sin ver (el detalle está en el aviso flotante). */}
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={burbuja ? `Mensajes: ${burbuja}` : 'Mensajes'}
          title={describeAccesoInbox(alertas)}
          onClick={() => navigate(ROUTES.conversations)}
        >
          <MessageSquare size={18} />
          <InboxAlertsBubble total={alertas.nuevas.length} />
        </Button>

        {/* Sin módulo de notificaciones detrás: el bot pausado y las novedades del inbox ya salen en
            la burbuja de arriba y en el aviso flotante (ver `docs/INBOX.md`). */}
        <Button variant="ghost" size="icon" aria-label="Notificaciones" disabled title="Próximamente">
          <Bell size={18} />
        </Button>

        <span className="hidden h-8 w-px bg-line sm:block" aria-hidden="true" />

        <UserMenu />
      </div>
    </header>
  );
}
